import { useCallback, useMemo, useState } from 'react';
import { App, Button, Card, Form, Input, Space, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, StopOutlined, CheckOutlined, CopyOutlined, ReloadOutlined } from '@ant-design/icons';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '../../../lib/api';
import { useAdminAccess } from '../../../hooks/useAdminAccess';
import { ResponsiveTable, type ResponsiveColumnType } from '../../../components/common/responsive';
import { withDevModalFilePath } from '../../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/admin/settings/AccessControlTemplatesPage.tsx');

interface AccessControlTemplate {
  id: string;
  templateName: string;
  roleName: string;
  description?: string | null;
  permissions: string[];
  isArchived: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const normalizeAccessControlTemplate = (value: unknown): AccessControlTemplate | null => {
  if (
    !isRecord(value) ||
    typeof value.id !== 'string' ||
    typeof value.templateName !== 'string' ||
    typeof value.roleName !== 'string'
  ) {
    return null;
  }

  return {
    id: value.id,
    templateName: value.templateName,
    roleName: value.roleName,
    description: typeof value.description === 'string' ? value.description : null,
    permissions: Array.isArray(value.permissions)
      ? value.permissions.filter((permission): permission is string => typeof permission === 'string')
      : [],
    isArchived: typeof value.isArchived === 'boolean' ? value.isArchived : false,
    createdBy: typeof value.createdBy === 'string' ? value.createdBy : '',
    createdAt: typeof value.createdAt === 'string' ? value.createdAt : '',
    updatedAt: typeof value.updatedAt === 'string' ? value.updatedAt : '',
  };
};

const templateSchema = z.object({
  templateName: z.string().min(1, '名前を入力してください'),
  roleName: z.string().min(1, 'ロール名を入力してください'),
  description: z.string().optional(),
  permissions: z.string().min(1, '権限をカンマ区切りで入力してください'),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

export function AccessControlTemplatesPage() {
  const { message } = App.useApp();
  const { requireRole } = useAdminAccess();
  const allowed = requireRole(['system_admin']);
  const listQuery = trpc.accessControlTemplates.list.useQuery();
  const createMutation = trpc.accessControlTemplates.create.useMutation({
    onSuccess: () => {
      message.success('テンプレートを作成しました');
      listQuery.refetch();
      setModalOpen(false);
      form.reset();
    },
    onError: (error) => message.error(error?.message ?? 'テンプレートの作成に失敗しました'),
  });
  const updateMutation = trpc.accessControlTemplates.update.useMutation({
    onSuccess: () => {
      message.success('テンプレートを更新しました');
      listQuery.refetch();
      setModalOpen(false);
      setEditingTemplate(null);
    },
    onError: (error) => message.error(error?.message ?? 'テンプレートの更新に失敗しました'),
  });
  const archiveMutation = trpc.accessControlTemplates.setArchived.useMutation({
    onSuccess: () => {
      message.success('テンプレートの公開状態を更新しました');
      listQuery.refetch();
    },
    onError: (error) => message.error(error?.message ?? '更新に失敗しました'),
  });
  const applyMutation = trpc.accessControlTemplates.applyToCompany.useMutation({
    onSuccess: () => {
      message.success('テンプレートを会社に適用しました');
      setApplyModalOpen(false);
      setApplyCompanyId('');
    },
    onError: (error) => message.error(error?.message ?? 'テンプレート適用に失敗しました'),
  });

  const [modalOpen, setModalOpen] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<AccessControlTemplate | null>(null);
  const [applyTemplateId, setApplyTemplateId] = useState<string | null>(null);
  const [applyCompanyId, setApplyCompanyId] = useState('');
  const [showArchived, setShowArchived] = useState(false);

  const form = useForm<TemplateFormValues>({ resolver: zodResolver(templateSchema) });

  const templates = useMemo(
    () => (listQuery.data ?? []).flatMap((template) => {
      const normalized = normalizeAccessControlTemplate(template);
      return normalized ? [normalized] : [];
    }),
    [listQuery.data]
  );

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => (showArchived ? true : !template.isArchived));
  }, [templates, showArchived]);

  const handleEdit = useCallback(
    (template: AccessControlTemplate) => {
      setEditingTemplate(template);
      form.setValue('templateName', template.templateName);
      form.setValue('roleName', template.roleName);
      form.setValue('description', template.description ?? '');
      form.setValue('permissions', template.permissions.join(', '));
      setModalOpen(true);
    },
    [form],
  );

  const handleApply = useCallback((templateId: string) => {
    setApplyTemplateId(templateId);
    setApplyModalOpen(true);
  }, []);

  const columns = useMemo<ResponsiveColumnType<AccessControlTemplate>[]>(
    () => [
      {
        title: '名前',
        dataIndex: 'templateName',
        key: 'templateName',
        render: (value: string) => <strong>{value}</strong>,
        mobileRole: 'title',
        mobilePriority: 1,
      },
      {
        title: 'ロール名',
        dataIndex: 'roleName',
        key: 'roleName',
        render: (value: string) => <Tag color="blue">{value}</Tag>,
        mobileRole: 'tag',
        mobilePriority: 2,
      },
      {
        title: '権限',
        dataIndex: 'permissions',
        key: 'permissions',
        render: (permissions: string[]) => (
          <Space wrap>
            {permissions.map((permission) => (
              <Tag key={permission} color="purple">{permission}</Tag>
            ))}
          </Space>
        ),
        mobileRole: 'description',
        mobilePriority: 3,
      },
      {
        title: '状態',
        dataIndex: 'isArchived',
        key: 'status',
        render: (archived: boolean) => (archived ? <Tag color="volcano">非公開</Tag> : <Tag color="green">公開</Tag>),
        mobileRole: 'tag',
        mobilePriority: 4,
      },
      {
        title: '操作',
        key: 'actions',
        render: (_: unknown, template) => (
          <Space>
            <Button icon={<EditOutlined />} onClick={() => handleEdit(template)}>編集</Button>
            <Button
              icon={template.isArchived ? <CheckOutlined /> : <StopOutlined />}
              onClick={() => archiveMutation.mutate({ id: template.id, archived: !template.isArchived })}
            >
              {template.isArchived ? '公開' : '非公開'}
            </Button>
            <Button icon={<CopyOutlined />} onClick={() => handleApply(template.id)}>会社にコピー</Button>
          </Space>
        ),
        mobileRole: 'hidden',
      },
    ],
    [archiveMutation, handleEdit, handleApply],
  );

  const handleSubmit = async (values: TemplateFormValues) => {
    const payload = {
      templateName: values.templateName,
      roleName: values.roleName,
      description: values.description ?? '',
      permissions: values.permissions.split(',').map((permission) => permission.trim()).filter(Boolean),
    };

    if (payload.permissions.length === 0) {
      message.error('1つ以上の権限を入力してください');
      return;
    }

    if (editingTemplate) {
      await updateMutation.mutateAsync({ id: editingTemplate.id, data: payload });
    } else {
      await createMutation.mutateAsync(payload);
    }
  };

  const handleApplySubmit = () => {
    if (!applyTemplateId) return;
    if (!applyCompanyId.trim()) {
      message.error('会社IDを入力してください');
      return;
    }
    applyMutation.mutate({ templateId: applyTemplateId, companyId: applyCompanyId.trim() });
  };

  if (!allowed) {
    return <Card>このページにアクセスする権限がありません</Card>;
  }

  return (
    <Card
      title="アクセス制御テンプレート"
      extra={
        <Space>
          <Switch checked={showArchived} onChange={setShowArchived} />
          <span>非公開を表示</span>
          <Button icon={<ReloadOutlined />} onClick={() => listQuery.refetch()} loading={listQuery.isFetching}>
            再読込
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { setEditingTemplate(null); form.reset(); setModalOpen(true); }}>
            テンプレート追加
          </Button>
        </Space>
      }
    >
      <ResponsiveTable<AccessControlTemplate>
        columns={columns}
        dataSource={filteredTemplates}
        rowKey="id"
        loading={listQuery.isLoading}
        mobileCardConfig={{
          titleField: 'templateName',
          actions: (template) => (
            <Space>
              <Button icon={<EditOutlined />} onClick={() => handleEdit(template)} size="small">編集</Button>
              <Button icon={<CopyOutlined />} onClick={() => handleApply(template.id)} size="small">コピー</Button>
            </Space>
          ),
        }}
      />

      <Modal
        title={editingTemplate ? 'テンプレート編集' : '作業指示書作成'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={form.handleSubmit(handleSubmit)}
        okButtonProps={{ loading: createMutation.isPending || updateMutation.isPending }}
        destroyOnHidden
      >
        <Form layout="vertical">
          <Form.Item label="名前" required>
            <Input {...form.register('templateName')} />
          </Form.Item>
          <Form.Item label="ロール名" required>
            <Input {...form.register('roleName')} />
          </Form.Item>
          <Form.Item label="説明">
            <Input.TextArea rows={3} {...form.register('description')} />
          </Form.Item>
          <Form.Item label="権限 (カンマ区切り)" required>
            <Input {...form.register('permissions')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="会社にテンプレートをコピー"
        open={applyModalOpen}
        onCancel={() => setApplyModalOpen(false)}
        onOk={handleApplySubmit}
        okButtonProps={{ loading: applyMutation.isPending }}
      >
        <p>テンプレートを適用する会社ID (UUID) を入力してください。</p>
        <Input
          value={applyCompanyId}
          onChange={(event) => setApplyCompanyId(event.target.value)}
          placeholder="00000000-0000-0000-0000-000000000000"
        />
      </Modal>
    </Card>
  );
}

